import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/** Tiempo visible de cada imagen antes de empezar a cambiar. */
const HOLD_MS = 1500;
/** Duración del crossfade (debe coincidir con CSS). */
const FADE_MS = 1500;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function canHoverFine() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}

function formatPrice(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(value);
}

function ProductCard({ product }) {
  const images = (
    product.imagenes?.length
      ? product.imagenes
      : product.imagen_principal
        ? [product.imagen_principal]
        : []
  ).filter(Boolean);

  const [layerA, setLayerA] = useState(images[0] || null);
  const [layerB, setLayerB] = useState(images[1] || images[0] || null);
  const [showA, setShowA] = useState(true);
  const [hovered, setHovered] = useState(false);
  const indexRef = useRef(0);
  const showARef = useRef(true);
  const imagesRef = useRef(images);
  const holdTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);

  imagesRef.current = images;

  const imagesKey = images.join('|');

  useEffect(() => {
    setLayerA(images[0] || null);
    setLayerB(images[1] || images[0] || null);
    setShowA(true);
    indexRef.current = 0;
    showARef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when gallery URLs change
  }, [product.leora_productos_UUID, imagesKey]);

  const clearTimers = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const advanceToNext = useCallback(() => {
    const list = imagesRef.current;
    if (list.length <= 1) return;
    const next = (indexRef.current + 1) % list.length;
    const nextSrc = list[next];
    // Cargar src en la capa oculta y luego activar el fade (doble rAF).
    if (showARef.current) {
      setLayerB(nextSrc);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShowA(false);
          showARef.current = false;
        });
      });
    } else {
      setLayerA(nextSrc);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShowA(true);
          showARef.current = true;
        });
      });
    }
    indexRef.current = next;
  }, []);

  const scheduleCycle = useCallback(() => {
    clearTimers();
    // Hold current image, then start fade, then wait for fade to finish before next hold.
    holdTimerRef.current = setTimeout(() => {
      advanceToNext();
      fadeTimerRef.current = setTimeout(() => {
        scheduleCycle();
      }, FADE_MS);
    }, HOLD_MS);
  }, [advanceToNext, clearTimers]);

  const onEnter = () => {
    if (!canHoverFine()) return;
    setHovered(true);
    if (images.length <= 1 || prefersReducedMotion()) return;
    scheduleCycle();
  };

  const onLeave = () => {
    setHovered(false);
    clearTimers();
    indexRef.current = 0;
    showARef.current = true;
    setShowA(true);
    setLayerA(images[0] || null);
    setLayerB(images[1] || images[0] || null);
  };

  useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <Link
      to={`/productos/${product.leora_productos_UUID}`}
      className={`product-card${hovered ? ' product-card--hover' : ''}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="product-card-image-wrap">
        {images.length > 0 ? (
          <>
            {layerA ? (
              <img
                src={layerA}
                alt={showA ? product.nombre : ''}
                className={`product-card-image${showA ? ' is-active' : ''}`}
                loading="lazy"
              />
            ) : null}
            {layerB && images.length > 1 ? (
              <img
                src={layerB}
                alt={!showA ? product.nombre : ''}
                aria-hidden={showA}
                className={`product-card-image${!showA ? ' is-active' : ''}`}
                loading="lazy"
              />
            ) : null}
          </>
        ) : (
          <div className="product-card-image-placeholder" />
        )}
        {product.estado === 'agotado' && (
          <span className="product-card-badge">Agotado</span>
        )}
      </div>
      <div className="product-card-info">
        <span className="product-card-brand">{product.marca}</span>
        <span className="product-card-name">{product.nombre}</span>
        <span className="product-card-price">
          {formatPrice(product.precio_minimo)}
        </span>
      </div>
    </Link>
  );
}

export default ProductCard;
